/* tslint:disable */
/* eslint-disable */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Theme,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { Person, VpnKey, Upgrade, CheckCircle, ChevronRight, SupervisorAccount, People } from '@material-ui/icons';
import { useCommonStyles } from './AdminConsoleBilling';
import { useHistory } from 'react-router';
import { useCampfireLazyQuery } from '../../../../../global/network/useCampfireLazyQuery';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import {
  GetBillingPackageOptions,
  GetBillingPackageOptionsVariables,
  GetBillingPackageOptions_billingPackageOptions as BillingPackageOptionType,
} from '../../../../subscriptions/prices/__generated__/GetBillingPackageOptions';
import { GET_PRICES } from '../../../../subscriptions/prices/price-model.gql';

import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import pluralize from 'pluralize';
import { BillingCurrency, getDisplayPrice } from './billing-currency';

type Props = {
  product: any | null;
  price: any | null;
  tier: any | null;
  currency: BillingCurrency;
  taxLabel?: string;
  isCustomPackage?: boolean;
};

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      borderRadius: '8px',
    },
    lightItem: {
      background: theme.color.grey.neutralBrand100,
      padding: '16px',
      borderRadiusTopLeft: '8px',
      borderRadiusTopRight: '8px',
    },
    darkItem: {
      background: theme.color.grey.neutralBrand200,
      padding: '16px',
      borderRadiusBottomLeft: '8px',
      borderRadiusBottomRight: '8px',
    },
    title: {
      color: theme.color.grey.neutralBrand900,
      fontWeight: 700,
      fontSize: 18,
      lineSpacing: '-2%',
    },
    subtitle: {
      color: theme.color.grey.neutralBrand500,
      fontWeight: 700,
      fontSize: 16,
      lineHeight: '22px',
      lineSpacing: '-2%',
    },
    description: {
      fontSize: 14,
    },
    extraTitle: {
      color: theme.color.grey.neutralBrand500,
      fontWeight: 700,
      fontSize: 16,
    },
    extraPrice: {
      fontWeight: 400,
      fontSize: 11,
    },
    planCard: {
      border: '2px solid #f0f0f0',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px',
      cursor: 'pointer',
      transition: 'border-color 0.2s ease',
      '&:hover': {
        borderColor: '#ddd',
      },
    },
    planCardSelected: {
      borderColor: theme.palette.primary.main,
      backgroundColor: 'rgba(63, 81, 181, 0.04)',
    },
    planTitle: {
      fontWeight: 600,
      fontSize: '16px',
      marginBottom: '4px',
    },
    planPrice: {
      fontWeight: 700,
      fontSize: '20px',
      color: theme.palette.primary.main,
      marginBottom: '8px',
    },
    planFeature: {
      fontSize: '13px',
      color: '#666',
      marginBottom: '4px',
      display: 'flex',
      alignItems: 'center',
    },
    upgradeButton: {
      marginTop: '16px',
      textTransform: 'none',
      borderRadius: '4px',
      fontWeight: 600,
    },
  })
);

const PlanSelection = ({
  onSelectPlan,
  currency,
}: {
  onSelectPlan: (planId: string) => void;
  currency: BillingCurrency;
}) => {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [getBillingPackageOptions, { loading }] = useCampfireLazyQuery<
    GetBillingPackageOptions,
    GetBillingPackageOptionsVariables
  >(GET_PRICES);
  const [planOptions, setPlanOptions] = useState<BillingPackageOptionType[]>([]);
  const apiUrl = useApiUrl();
  const campfireCheckout = useCampfireFetch<{ url: string }>({ defer: true });

  useEffect(() => {
    getBillingPackageOptions({ variables: {} }).then((response) => {
      setPlanOptions(response.data?.billingPackageOptions || []);
      if (response.data?.billingPackageOptions?.[0]) {
        setSelectedPlan(response.data.billingPackageOptions[0].billingPackageOptionId);
      }
    });
  }, [getBillingPackageOptions]);

  const handleSelectPlan = () => {
    if (!selectedPlan) return;

    campfireCheckout
      .run({
        url: `${apiUrl}/tm/subscriptions/checkout`,
        method: 'post',
        data: {
          priceId: selectedPlan,
        },
      })
      .then((response) => {
        const { url } = response.data.data;
        window.location.replace(url);
      })
      .catch((error) => {
        console.error('Checkout error:', error);
      });
  };

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' py={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant='body2' style={{ marginBottom: '16px', color: '#666' }}>
        Choose a plan to get started:
      </Typography>

      {planOptions.map((option) => {
        let stripePrice;
        try {
          stripePrice = JSON.parse(option.stripePrice || '{}');
        } catch {
          stripePrice = { unitAmount: 0, recurring: { interval: 'month' } };
        }

        let stripeProduct;
        try {
          stripeProduct = JSON.parse(option.billingPackage.stripeProduct || '{}');
        } catch {
          stripeProduct = { name: 'Volaby Plan' };
        }

        const isSelected = option.billingPackageOptionId === selectedPlan;
        const tier = option.billingPackage.tier;
        const displayPrice = getDisplayPrice((stripePrice.unit_amount || stripePrice.unitAmount) / 100 || 0, currency);

        return (
          <Box
            key={option.billingPackageOptionId}
            className={`${classes.planCard} ${isSelected ? classes.planCardSelected : ''}`}
            onClick={() => setSelectedPlan(option.billingPackageOptionId)}
          >
            <Box display='flex' justifyContent='space-between' alignItems='flex-start'>
              <Box>
                <Typography className={classes.planTitle}>{stripeProduct.name || tier?.name || 'Plan'}</Typography>
                <Typography className={classes.planPrice}>
                  {displayPrice.code} {displayPrice.symbol}
                  {displayPrice.amount}/{stripePrice?.recurring?.interval || 'month'}
                </Typography>

                {tier && (
                  <Box>
                    <Box className={classes.planFeature}>
                      <CheckCircle
                        style={{ fontSize: '14px', marginRight: '6px', color: theme.palette.success.main }}
                      />
                      Up to {tier.volunteerLimit} volunteers
                    </Box>
                    <Box className={classes.planFeature}>
                      <CheckCircle
                        style={{ fontSize: '14px', marginRight: '6px', color: theme.palette.success.main }}
                      />
                      {tier.adminLimit} admin users
                    </Box>
                    <Box className={classes.planFeature}>
                      <CheckCircle
                        style={{ fontSize: '14px', marginRight: '6px', color: theme.palette.success.main }}
                      />
                      {tier.programManagerLimit} program managers
                    </Box>
                  </Box>
                )}
              </Box>

              <Radio checked={isSelected} color='primary' size='small' />
            </Box>
          </Box>
        );
      })}

      <Button
        variant='contained'
        color='primary'
        fullWidth
        onClick={handleSelectPlan}
        disabled={!selectedPlan || campfireCheckout.isLoading}
        endIcon={<ChevronRight />}
        className={classes.upgradeButton}
      >
        {campfireCheckout.isLoading ? 'Processing...' : 'Continue to Payment'}
      </Button>
    </Box>
  );
};

export const BillingPlan = (props: Props) => {
  const commonClasses = useCommonStyles();
  const classes = useStyles();
  const { theme } = useCampfireTheme();

  const { tier, product, price, currency, taxLabel, isCustomPackage } = props;
  const displayPrice = getDisplayPrice((price?.unit_amount || price?.unitAmount) / 100 || 0, currency);

  const interval = price?.recurring?.interval || '';
  const recurring = interval;

  // Check if user has no plan (trial or no subscription) and is not a custom package
  const hasNoPlan = !product && !tier && !isCustomPackage;

  // TODO: Calculate actual extras based on current usage vs plan limits
  const extras: any[] = [];

  if (hasNoPlan) {
    return (
      <Box>
        <Typography className={commonClasses.sectionHeading}>Choose Your Plan</Typography>
        <PlanSelection onSelectPlan={(planId) => console.log('Selected plan:', planId)} currency={currency} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography className={commonClasses.sectionHeading}>Your Plan</Typography>
      <Box mt={2}>
        <Typography style={{ fontWeight: 600, fontSize: '14px', color: '#333' }}>
          {isCustomPackage ? 'Custom' : product?.name || tier?.name || 'Free Trial'}
        </Typography>
        <Typography style={{ fontWeight: 400, fontSize: '14px', color: '#666', marginTop: '4px' }}>
          {isCustomPackage ? (
            'Custom pricing arrangement'
          ) : (
            <>
              <span style={{ fontSize: '12px', color: '#666', fontWeight: 600 }}>{displayPrice.code}</span>
              {displayPrice.symbol}
              {displayPrice.amount}/{recurring}
              {(price?.unit_amount || price?.unitAmount) && taxLabel && (
                <span style={{ fontSize: '12px', color: '#999', marginLeft: '8px' }}>{taxLabel}</span>
              )}
            </>
          )}
        </Typography>

        {!isCustomPackage && tier && (
          <Box mt={3}>
            <Typography style={{ fontWeight: 500, fontSize: '14px', marginBottom: '8px' }}>Plan Includes</Typography>
            <Box display='flex' flexDirection='row'>
              {tier.volunteerLimit && (
                <Box display='flex' alignItems='center' mr={3}>
                  <Box display='flex' flexDirection='column' alignItems='center' mr={1} width='32px'>
                    <People style={{ fontSize: '16px', color: theme.color.grey.neutralBrand500 }} />
                    <Typography
                      style={{
                        fontSize: '8px',
                        color: theme.color.grey.neutralBrand500,
                        fontWeight: 500,
                        lineHeight: 1,
                      }}
                    >
                      VOL
                    </Typography>
                  </Box>
                  <Box>
                    <Typography style={{ fontSize: '12px', color: '#333', fontWeight: 600 }}>
                      {tier.volunteerLimit}
                    </Typography>
                  </Box>
                </Box>
              )}
              {tier.adminLimit && (
                <Box display='flex' alignItems='center' mr={3}>
                  <Box display='flex' flexDirection='column' alignItems='center' mr={1} width='32px'>
                    <SupervisorAccount style={{ fontSize: '16px', color: theme.color.grey.neutralBrand500 }} />
                    <Typography
                      style={{
                        fontSize: '8px',
                        color: theme.color.grey.neutralBrand500,
                        fontWeight: 500,
                        lineHeight: 1,
                      }}
                    >
                      ADM
                    </Typography>
                  </Box>
                  <Box>
                    <Typography style={{ fontSize: '12px', color: '#333', fontWeight: 600 }}>
                      {tier.adminLimit}
                    </Typography>
                  </Box>
                </Box>
              )}
              {tier.programManagerLimit && (
                <Box display='flex' alignItems='center' mr={3}>
                  <Box display='flex' flexDirection='column' alignItems='center' mr={1} width='32px'>
                    <Person style={{ fontSize: '16px', color: theme.color.grey.neutralBrand500 }} />
                    <Typography
                      style={{
                        fontSize: '8px',
                        color: theme.color.grey.neutralBrand500,
                        fontWeight: 500,
                        lineHeight: 1,
                      }}
                    >
                      PM
                    </Typography>
                  </Box>
                  <Box>
                    <Typography style={{ fontSize: '12px', color: '#333', fontWeight: 600 }}>
                      {tier.programManagerLimit}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {!isCustomPackage && extras.length > 0 && (
          <Box mt={3}>
            <Typography style={{ fontWeight: 500, fontSize: '14px', marginBottom: '8px' }}>Extras</Typography>
            <Box display='flex' flexDirection='row'>
              {extras.map((extra, index) => (
                <Box display='flex' alignItems='center' key={index} mr={3}>
                  <Box display='flex' flexDirection='column' alignItems='center' mr={1} width='32px'>
                    {extra.icon}
                    <Typography
                      style={{
                        fontSize: '8px',
                        color: theme.color.grey.neutralBrand500,
                        fontWeight: 500,
                        lineHeight: 1,
                      }}
                    >
                      {extra.abbreviation}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography style={{ fontSize: '12px', color: '#333', fontWeight: 600 }}>+{extra.count}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
